import React, {useState, useRef, useCallback} from 'react';
import {Button, Card, Col, DatePicker, Form, InputNumber, message, Row, Select, Space, Table} from "antd";
import {useSelector} from "react-redux";
import {useNewIdleFuelMutation} from "../../../slices/rootApiSlices/reportApiSlice";
import jsPDF from "jspdf";
import SelectVehicle from "./SelectVehicle";
import {DownloadOutlined, FilePdfOutlined, FileTextOutlined, SearchOutlined} from "@ant-design/icons";
import Title from "../../../components/Title";
import 'jspdf-autotable';
import * as XLSX from "xlsx";
import moment from "moment";

const IdleFuelReport = () => {

    const {Option} = Select;
    const [tableData, setTableData] = useState([]);
    const [infoData, setInfoData] = useState({});
    const {RangePicker} = DatePicker;
    const [endDate, setEndDate] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [distanceDifference, setDistanceDifference] = useState(null);
    const [fuelDifference, setFuelDifference] = useState(null);
    const [selectedOperator, setSelectedOperator] = useState("");
    const searchInput = useRef(null);
    const [vehicle_id, setVehicleID] = useState('');
    const {user} = useSelector((state) => state.auth);
    const [fuelHandle, {isLoading}, error] = useNewIdleFuelMutation();

    function disabledDate(current) {
        const tomorrow = moment().add(1, 'days').startOf('day');
        return current && current >= tomorrow;
    }

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 30,
        total: 0,
    });


    const handleQuery = useCallback(async () => {
        try {
            const res = await fuelHandle({
                id: vehicle_id,
                token: user?.token,
                startDate,
                endDate,
                resolution: '8s'
            }).unwrap();
            if (res?.data?.length > 0) {
                const findFirstValidValue = (data, key) => {
                    for (let i = 0; i < data.length; i++) {
                        const value = parseFloat(data[i]?.[key]);
                        if (!isNaN(value)) return value;
                    }
                    return 0;
                };
                const findLastValidValue = (data, key) => {
                    for (let i = data.length - 1; i >= 0; i--) {
                        const value = parseFloat(data[i]?.[key]);
                        if (!isNaN(value)) return value;
                    }
                    return 0;
                };
                const firstDistance = findFirstValidValue(res.data, 'highresvehicletotaldistance');
                const lastDistance = findLastValidValue(res.data, 'highresvehicletotaldistance');
                const firstFuel = findFirstValidValue(res.data, 'liquetenginetotalfuelused');
                const lastFuel = findLastValidValue(res.data, 'liquetenginetotalfuelused');
                const foundDistanceDifference = lastDistance - firstDistance;
                const foundFuelDifference = lastFuel - firstFuel;
                setDistanceDifference(foundDistanceDifference);
                setFuelDifference(foundFuelDifference.toFixed(2));
                setInfoData(res.data[0]);
                setTableData(res.data);
            } else {
                message.warn("Veri bulunamadı.");
            }
            message.success("İşlem Başarılı..");
        } catch (e) {
            message.error(
                e?.data?.messageTR ||
                "Bir hata oluştu. Lütfen bir süre sonra tekrar deneyiniz."
            );
        }
    }, [fuelHandle, startDate, endDate, user?.token, vehicle_id, pagination]);

    function handleDateChange(date, dateString) {
        const adjustDate = (dateStr) => {
            const date = new Date(dateStr.replace(' ', 'T') + '.000Z');
            date.setHours(date.getHours() - 3); // Subtract 3 hours
            return date.toISOString().slice(0, 19).replace('T', ' ');
        };

        const startDate = adjustDate(dateString[0]);
        const endDate = adjustDate(dateString[1]);

        setStartDate(startDate);
        setEndDate(endDate);
    }

    const handleReset = (clearFilters) => {
        clearFilters();
        setSelectedOperator('');
        handleQuery();
    };

    const onPageChange = (page, pageSize) => {
        setPagination({current: page, pageSize: pageSize});
    };

    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({setSelectedKeys, selectedKeys, confirm, clearFilters, close}) => (
            <div style={{padding: 8}}>
                <div style={{marginBottom: 8, display: 'flex'}}>
                    <InputNumber
                        style={{width: '49%', marginRight: 5}}
                        placeholder="Değer"
                        value={selectedKeys[0]}
                        onChange={(value) => setSelectedKeys(value ? [value] : [])}
                    />
                    <Select
                        style={{width: '49%'}}
                        onChange={(value) => setSelectedOperator(value)}
                        placeholder="lütfen seçin"
                        value={selectedOperator}
                    >
                        <Option value=">">Büyüktür</Option>
                        <Option value="<">Küçüktür</Option>
                        <Option value="=">Eşittir</Option>
                    </Select>
                </div>
                <Space>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{width: 90}}
                    >
                        Değeri Sil
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({
                                closeDropdown: false,
                            });
                        }}
                    >
                        Filtrele
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        Kapat
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => (
            <SearchOutlined
                style={{
                    color: filtered ? '#1677ff' : undefined,
                }}
            />
        ),
        onFilter: (value, record) => {
            const dataIndexValue = parseFloat(record[dataIndex]);
            const filterValue = parseFloat(value);
            if (isNaN(dataIndexValue) || isNaN(filterValue)) {
                return false;
            }
            switch (selectedOperator) {
                case '>':
                    return dataIndexValue > filterValue;
                case '<':
                    return dataIndexValue < filterValue;
                case '=':
                    return dataIndexValue === filterValue;
                default:
                    return false;
            }
        },
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) => text,
    });

    const columns = [
        {
            title: 'Oluşturulma Tarihi',
            dataIndex: 'createdat',
            key: 'createdat',
            render: (text) => {
                const date = new Date(text);
                date.setHours(date.getHours() + 3);
                const adjustedDate = date.toISOString().slice(0, 19).replace('T', ' ');
                return <span>{adjustedDate}</span>;
            },
        },
        {
            title: 'Yakit Seviyesi (%)',
            dataIndex: 'fuellevel',
            key: 'fuellevel',
            ...getColumnSearchProps('fuellevel'),
            sorter: (a, b) => a.fuellevel - b.fuellevel,
            render: (text) => (
                <span>
                    {isNaN(parseFloat(text)) ? '-' : text.toFixed(1)}
                </span>
            ),
        },
        {
            title: "Toplam Yakıt Tüketimi",
            dataIndex: "liquetenginetotalfuelused",
            key: "liquetenginetotalfuelused",
        },
        {
            title: "Anlık Yakıt Tüketimi",
            dataIndex: "fuelrate",
            key: "fuelrate",
        },
        {
            title: "Toplam KM",
            dataIndex: "highresvehicletotaldistance",
            key: "highresvehicletotaldistance",
        },
        {
            title: "Ortalama Yakıt Tüketimi",
            dataIndex: "avgFuelUsed",
            key: "avgFuelUsed",
        },
        {
            title: 'Konum',
            dataIndex: 'gps',
            key: 'gps',
            fixed: 'left',
            render: (gps) => (
                <span>
                    {gps !== null && gps !== undefined && gps.lat && gps.lon ?
                        `Enlem: ${gps.lat}, Boylam: ${gps.lon}` : '-'}
                </span>
            )
        },
    ];

    const generatePDF = () => {

        const doc = new jsPDF('landscape', 'mm', 'a3');

        const kapıNumarası = infoData?.operation_number ? infoData?.operation_number : '-';
        const araçPlakası = infoData?.plate_number ? infoData?.plate_number : '-';
        const raporTarihAralıkları = startDate && endDate
            ? `${moment(startDate).add(3, 'hours').format('DD/MM/YYYY HH:mm:ss')} - ${moment(endDate).add(3, 'hours').format('DD/MM/YYYY HH:mm:ss')}`
            : '-';
        const sürücüAdıSoyadı = infoData?.name ? `${infoData?.name} ${infoData?.surname}` : 'Tanimlanmadi';
        const chassisNumber = infoData?.chassisnumber ? infoData?.chassisnumber : 'Tanimlanmadi';
        const raporOluşturulmaTarihi = moment().format('DD.MM.YYYY HH:mm');
        const raporuOluşturan = user ? user?.name : '-';

        doc.setFontSize(12);

        doc.text(`Kapi Numarasi: ${kapıNumarası}`, 15, 10);
        doc.text(`Rapor Tarih Araliklari: ${raporTarihAralıkları}`, 120, 10);
        doc.text(`Arac Plakasi: ${araçPlakası}`, 15, 15);
        doc.text(`Surucu Adi / Soyadi: ${sürücüAdıSoyadı}`, 120, 15);
        doc.text(`Sase Numarasi: ${chassisNumber}`, 15, 20);
        doc.text(`Hat Numarasi :  Tanimlanmadi `, 120, 20);
        doc.text(`Rapor Olusturulma Tarihi: ${raporOluşturulmaTarihi}`, 15, 25);
        doc.text(`Raporu Olusturan: ${raporuOluşturan}`, 120, 25);
        doc.text(`Toplam KM: ${distanceDifference}`, 15, 30);
        doc.text(`Toplam Yakit Tuketimi [L] : ${fuelDifference}`, 120, 30);

        const formatGps = (gps) => {
            if (!gps || isNaN(gps.lat) || isNaN(gps.lon)) {
                return 'Tanimlanmadi';
            }
            return `Enlem: ${gps.lat}, Boylam: ${gps.lon}`;
        };

        doc.autoTable({
            head: [['Olusturulma Tarih/Saat', 'Yakit Seviyesi (%)', 'Toplam Yakit Tuketimi (L)', 'Anlik Yakit Tüketimi', 'Toplam KM', 'Ortalama Yakit Tuketimi (L)', 'Konum']],
            body: tableData.map((item) => [
                (() => {
                    const date = new Date(item.createdat);
                    date.setHours(date.getHours() + 3);
                    return date.toISOString().slice(0, 19).replace('T', ' ');
                })(),
                isNaN(item.fuellevel) ? '0' : item.fuellevel ? item.fuellevel.toFixed(1) : ' ',
                isNaN(item.liquetenginetotalfuelused) ? 0 : item.liquetenginetotalfuelused,
                isNaN(item.fuelrate) ? 0 : item.fuelrate,
                isNaN(item.highresvehicletotaldistance) ? 0 : item.highresvehicletotaldistance,
                isNaN(item.avgFuelUsed) ? 0 : item.avgFuelUsed,
                formatGps(item.gps)
            ]),
            startY: 40,
        });

        doc.save('Yakıt_Tuketim_Raporu.pdf');
    };

    const handleExcel = () => {
        const sanitizedOperationNumber = tableData[0]?.operation_number ? tableData[0].operation_number.replace(/\n/g, '') : '-';
        const plateNumber = infoData?.plate_number || '-';
        const chassisNumber = infoData?.chassisnumber || '-';
        const start_date = startDate ? moment.utc(startDate).add(3, 'hours').format('DD/MM/YYYY HH:mm:ss') : 'Tanımlanmadı';
        const end_date = endDate ? moment.utc(endDate).add(3, 'hours').format('DD/MM/YYYY HH:mm:ss') : 'Tanımlanmadı';
        const reportCreationDate = moment().format('DD/MM/YYYY HH:mm:ss');
        const driver = infoData?.name && infoData?.surname ? `${infoData.name} ${infoData.surname}` : 'Tanımlanmadı';

        const processedData = tableData.map((item) => {
            const date = new Date(item.createdat);
            date.setHours(date.getHours() + 3);
            const adjustedDate = date.toISOString().slice(0, 19).replace('T', ' ');

            // Konum bilgisi HYPERLINK formülüyle eklendi
            const locationLink = item.gps && !isNaN(item.gps.lat) && !isNaN(item.gps.lon)
                ? { hyperlink: `https://www.google.com/maps?q=${item.gps.lat},${item.gps.lon}`, text: "Haritada Gör" }
                : { text: "Tanımlanmadı" };

            return {
                'Oluşturulma Tarihi': adjustedDate || '',  // Eğer adjustedDate varsa yaz, yoksa boş bırak
                'Yakıt Seviyesi (%)': item.fuellevel ? item.fuellevel : '',  // Eğer fuellevel varsa yaz, yoksa boş bırak
                "Toplam Yakıt Tüketimi": item.liquetenginetotalfuelused ? item.liquetenginetotalfuelused : '',  // Eğer total fuel used varsa yaz, yoksa boş bırak
                "Anlık Yakıt Tüketimi ": item.fuelrate || '',  // Eğer fuelrate varsa yaz, yoksa boş bırak
                "Toplam KM": item.highresvehicletotaldistance || '',  // Eğer total distance varsa yaz, yoksa boş bırak
                "Ortalama Yakıt Tüketimi": item.avgFuelUsed || '',  // Eğer avgFuelUsed varsa yaz, yoksa boş bırak
                "Konum": locationLink || ''  // Eğer locationLink varsa yaz, yoksa boş bırak
            };

        });

        const worksheet = XLSX.utils.json_to_sheet([]);

        XLSX.utils.sheet_add_aoa(worksheet, [
            ['Kapı Numarası:', sanitizedOperationNumber, '', 'Rapor Tarih Aralıkları:', `${start_date} - ${end_date}`],
            ['Araç Plakası:', plateNumber, '', 'Sürücü Adı / Soyadı:', driver],
            ['Şase Numarası:', chassisNumber, '', 'Hat Numarası:', 'Tanımlanmadı'],
            ['Rapor Oluşturulma Tarihi:', reportCreationDate, '', 'Raporu oluşturan:', user?.name],
            ['Toplam KM :', distanceDifference, '', 'Toplam Yakıt Tüketimi [L]:', fuelDifference],
        ], { origin: 'A1' });

        XLSX.utils.sheet_add_json(worksheet, processedData, { origin: 'A7', skipHeader: false });

        // HYPERLINK formülünü "Konum" sütununa ekleyin
        processedData.forEach((item, index) => {
            if (item.Konum.hyperlink) {
                const rowNumber = index + 7; // "A7" satırından itibaren veriler başlıyor
                worksheet[`G${rowNumber}`] = { f: `HYPERLINK("${item.Konum.hyperlink}", "${item.Konum.text}")` };
            }
        });

        // Dinamik sütun genişlikleri
        const maxLengths = processedData.reduce((acc, curr) => {
            Object.keys(curr).forEach((key, index) => {
                const length = curr[key]?.toString().length || 15;
                acc[index] = Math.max(acc[index] || 15, length);
            });
            return acc;
        }, []);

        worksheet['!cols'] = maxLengths.map(len => ({ wch: len + 1 }));

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Yakıt Tüketim Raporu');

        XLSX.writeFile(workbook, 'Yakıt_Tuketim_Raporu.xlsx');
    };

    return (
        <Row justify="center">
            <Title title="Octoopi Cocpit | Yakıt Tüketim Raporu" description="Mertech Arge A.Ş Yakıt Tüketim Raporu"/>
            <Col xs={24} sm={24} md={24} lg={24} xl={16} xxl={12}>
                <Card title="Yakıt Tüketim Raporu" type="inner" size="small" style={{marginBottom: 10}}>
                    <SelectVehicle name="vehicleID" onChange={(e) => setVehicleID(e)}/>
                    <Form.Item name="byDate" label="Tarih Aralığı">
                        <RangePicker
                            showTime disabled={isLoading}
                            disabledDate={disabledDate}
                            placeholder={['Başlangıç Tarihi', 'Bitiş Tarihi']}
                            style={{margin: "auto", marginRight: 3, width: "100%"}}
                            onChange={handleDateChange}
                        />
                    </Form.Item>
                    <Button
                        type="primary"
                        icon={<DownloadOutlined/>}
                        onClick={handleQuery}
                        style={{marginTop: '16px'}}
                        loading={isLoading}
                        disabled={!vehicle_id}
                    >
                        Rapor Oluştur
                    </Button>
                </Card>
            </Col>
            <Col xs={24} sm={24} md={24} lg={24} xl={20} xxl={24}>
                <Card
                    type="inner"
                    title="Sonuçlar"
                    size="small"
                    extra={
                        <>
                            <Button
                                style={{marginRight: 5}}
                                size="small"
                                disabled={!tableData.length}
                                type="primary"
                                icon={<FileTextOutlined/>}
                                onClick={handleExcel}
                            >Excel</Button>
                            <Button
                                size="small"
                                disabled={!tableData.length}
                                type="primary"
                                icon={<FilePdfOutlined/>}
                                onClick={generatePDF}
                            >Pdf</Button>
                        </>
                    }
                >
                    <Table
                        rowKey="id"
                        loading={isLoading}
                        bordered={true}
                        size="small"
                        columns={columns}
                        dataSource={tableData}
                        error={error}
                        pagination={{
                            current: pagination.current,
                            pageSize: pagination.pageSize,
                            total: pagination.total,
                            onChange: onPageChange,
                        }}
                    />
                </Card>
            </Col>
        </Row>
    );

};

export default IdleFuelReport;
